import { DATE_PATTERN } from '../model/constants';

/** Pure calendar-date arithmetic on `YYYY-MM-DD` strings. No time zone is involved. */

const MS_PER_DAY = 86_400_000;

export interface CalendarDate {
  readonly year: number;
  readonly month: number;
  readonly day: number;
}

export function isValidDate(value: string): boolean {
  if (!DATE_PATTERN.test(value)) return false;
  const { year, month, day } = splitDate(value);
  const utc = new Date(Date.UTC(year, month - 1, day));
  return utc.getUTCFullYear() === year && utc.getUTCMonth() === month - 1 && utc.getUTCDate() === day;
}

function splitDate(value: string): CalendarDate {
  return {
    year: Number(value.slice(0, 4)),
    month: Number(value.slice(5, 7)),
    day: Number(value.slice(8, 10)),
  };
}

export function parseDate(value: string): CalendarDate {
  if (!isValidDate(value)) throw new RangeError(`Invalid date: ${value}`);
  return splitDate(value);
}

export function formatDate({ year, month, day }: CalendarDate): string {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Days since 1970-01-01 (negative before). */
export function epochDayOf(date: string): number {
  const { year, month, day } = parseDate(date);
  return Math.round(Date.UTC(year, month - 1, day) / MS_PER_DAY);
}

export function dateOfEpochDay(epochDay: number): string {
  const utc = new Date(epochDay * MS_PER_DAY);
  return formatDate({
    year: utc.getUTCFullYear(),
    month: utc.getUTCMonth() + 1,
    day: utc.getUTCDate(),
  });
}

export function addDays(date: string, days: number): string {
  return dateOfEpochDay(epochDayOf(date) + days);
}

/** Whole days from `from` to `to` (positive when `to` is later). */
export function daysBetween(from: string, to: string): number {
  return epochDayOf(to) - epochDayOf(from);
}

/** Day of week, Monday = 0 ... Sunday = 6. */
export function weekdayIndex(date: string): number {
  // 1970-01-01 was a Thursday (index 3).
  return (((epochDayOf(date) + 3) % 7) + 7) % 7;
}

/** The Monday of the week containing `date`. */
export function weekStartOf(date: string): string {
  return addDays(date, -weekdayIndex(date));
}

/** The 7 dates of the week starting at `weekStart`. */
export function weekDates(weekStart: string): string[] {
  return datesInRange(weekStart, addDays(weekStart, 6));
}

/** All dates from `from` to `to`, both inclusive; empty when `to` is before `from`. */
export function datesInRange(from: string, to: string): string[] {
  const first = epochDayOf(from);
  const last = epochDayOf(to);
  const out: string[] = [];
  for (let day = first; day <= last; day += 1) out.push(dateOfEpochDay(day));
  return out;
}
